import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DataTable from '../../components/design/DataTable.jsx';
import { createVideoTemplate, listVideoTemplates, updateVideoTemplate } from '../../services/contentAiService';

function safeJsonParse(text) {
  try {
    const v = JSON.parse(String(text || ''));
    if (!v || typeof v !== 'object') return null;
    return v;
  } catch {
    return null;
  }
}

export default function TemplatesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [configJsonText, setConfigJsonText] = useState(JSON.stringify({ width: 1280, height: 720, fps: 30, sceneSeconds: 4, crf: 23, preset: 'veryfast' }, null, 2));

  const load = async () => {
    setLoading(true);
    try {
      const resp = await listVideoTemplates();
      const data = resp?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setConfigJsonText(JSON.stringify({ width: 1280, height: 720, fps: 30, sceneSeconds: 4, crf: 23, preset: 'veryfast' }, null, 2));
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row?.name || '');
    setDescription(row?.description || '');
    setConfigJsonText(JSON.stringify(row?.configJson || {}, null, 2));
    setOpen(true);
  };

  const save = async () => {
    const cfg = safeJsonParse(configJsonText);
    if (!name.trim()) {
      toast.error('name obrigatório');
      return;
    }
    if (!cfg) {
      toast.error('configJson inválido');
      return;
    }
    setLoading(true);
    try {
      if (editing?.id) {
        await updateVideoTemplate(editing.id, { name, description, configJson: cfg });
        toast.success('Template atualizado');
      } else {
        await createVideoTemplate({ name, description, configJson: cfg });
        toast.success('Template criado');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Templates de Vídeo</Typography>
        <Button variant="contained" onClick={openCreate} disabled={loading}>Novo template</Button>
      </Box>

      <DataTable
        rows={rows}
        loading={loading}
        columns={[
          { field: 'id', headerName: 'ID', width: 90 },
          { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
          { field: 'enabled', headerName: 'Ativo', width: 110, valueGetter: (p) => (p?.row?.enabled ? 'Sim' : 'Não') },
          { field: 'updatedAt', headerName: 'Atualizado', width: 180, valueGetter: (p) => (p?.row?.updatedAt ? new Date(p.row.updatedAt).toLocaleString() : '') },
          {
            field: 'actions',
            headerName: 'Ações',
            width: 140,
            sortable: false,
            renderCell: (params) => (
              <Button size="small" variant="outlined" onClick={() => openEdit(params.row)}>Editar</Button>
            )
          }
        ]}
        getRowId={(r) => r.id}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar template' : 'Novo template'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <TextField label="Nome" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
            <TextField label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
            <TextField
              label="Config JSON"
              value={configJsonText}
              onChange={(e) => setConfigJsonText(e.target.value)}
              fullWidth
              multiline
              minRows={10}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpen(false)} disabled={loading}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={loading}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
